import * as holidayjp from '@holiday-jp/holiday_jp';
import momentTimezone from 'moment-timezone';
import { basicSiteVariablesDefinition } from '../config/baseConfig';

export default class DateUtils {
  /**
   * 今日の日付をフォーマット化された文字列で返します。
   */
  public static strToday = (): string => {
    return momentTimezone()
      .tz('Asia/Tokyo')
      .format(basicSiteVariablesDefinition.linkageDateFormat);
  };

  /**
   * 指定された日数目の日付をフォーマット化された文字列で返します。
   * 例1）本日が金曜だとして、1を渡すと、同週の土曜日の日付が返ってきます。
   */
  public static offsetDaysToTargetDate = (days: number): string => {
    return momentTimezone
      .tz('Asia/Tokyo')
      .add(days, 'days')
      .format(basicSiteVariablesDefinition.linkageDateFormat);
  };

  /**
   * 指定された営業日目の日付をフォーマット化された文字列で返します。
   * 例1）本日が金曜だとして、1を渡すと、次週の月曜日の日付が返ってきます。
   * 例2）本日が土曜だとして、1を渡すと、次週の月曜日の日付が返ってきます。
   * @param businessDays
   * @param baseDate - counted as businessDay or holiday
   */
  public static businessDaysToTargetDate(businessDays: number, baseDate?: string | number | Date): string {
    const currentDate = baseDate ? momentTimezone(baseDate).tz('Asia/Tokyo') : momentTimezone.tz('Asia/Tokyo');

    let countBusinessDays = 0;
    while (countBusinessDays < businessDays) {
      currentDate.add(1, 'days'); // 参照先の日数がIncrementされる。
      const isBusinessDay =
        currentDate.day() !== 0 && currentDate.day() !== 6 && !holidayjp.isHoliday(currentDate.toDate());
      if (isBusinessDay) {
        countBusinessDays++;
      }
    }

    return currentDate.format(basicSiteVariablesDefinition.linkageDateFormat);
  }

  /**
   * 申込不可日を考慮した最短申込み可能日を計算します。
   * @param minDateOffset 申込可能日までの最短日数
   * @param noEntryDates 申込不可日のセット
   * @param startDate 開始日（デフォルト: 今日）
   * @returns 最短申込み可能日
   */
  public static calcMinEntryDateWithNoEntryDates(
    minDateOffset: number,
    noEntryDates: Set<string>,
    startDate?: string
  ): string {
    const today = startDate || DateUtils.strToday();
    let currentDate = momentTimezone(today).tz('Asia/Tokyo');

    // 申込不可日もオフセット日数に含める（単純に日数をカウント）
    let daysCount = 0;
    while (daysCount < minDateOffset) {
      currentDate = currentDate.add(1, 'day');
      daysCount++;
    }

    // 最終日が申込不可日の場合、次の非申込不可日まで進める
    while (noEntryDates.has(currentDate.format(basicSiteVariablesDefinition.linkageDateFormat))) {
      currentDate = currentDate.add(1, 'day');
    }

    return currentDate.format(basicSiteVariablesDefinition.linkageDateFormat);
  }

  /**
   * 最短申込可能日を計算します。
   * @param minDateOffset 最小日数オフセット
   * @param useBusinessDayOffset 営業日計算を使用するかフラグ
   * @param noEntryDatesSet 申込み不可日のSet
   * @param startDate 開始日
   * @returns 最短申込み可能日
   */
  public static calcMinDate(
    minDateOffset: number | undefined,
    useBusinessDayOffset: boolean | undefined,
    noEntryDatesSet: Set<string>,
    today: string
  ): string | undefined {
    if (!minDateOffset) {
      return undefined;
    }

    if (noEntryDatesSet.size > 0) {
      return DateUtils.calcMinEntryDateWithNoEntryDates(minDateOffset, noEntryDatesSet, today);
    } else {
      // 申込み不可日がない場合の従来の計算
      if (useBusinessDayOffset) {
        return DateUtils.businessDaysToTargetDate(minDateOffset);
      } else {
        return DateUtils.offsetDaysToTargetDate(minDateOffset);
      }
    }
  }
}
